import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import EmojiObjectsRoundedIcon from '@mui/icons-material/EmojiObjectsRounded';
import type {
  PracticeTrajectoryDto,
  DimensionTrajectoryDto,
  GrowthTrend,
  TopicMasteryStatus,
} from '../../../lib/api/socratic.api';

// "Mi progreso" — the student's self-referenced growth (3c-4 F2 + F3 topic mastery).
// Growth-mindset framing: shows advance, mastery and next challenge, never
// deficiency or ranking.
export function SocraticGrowthPanel({
  trajectory,
  topicTitles = {},
}: {
  trajectory: PracticeTrajectoryDto | null;
  topicTitles?: Record<string, string>;
}): JSX.Element | null {
  const { t } = useTranslation('sis');
  if (!trajectory) return null;

  if (trajectory.coldStart) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          {t('socraticPractice.progress.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('socraticPractice.progress.coldStart', {
            n: trajectory.assessedCount,
            min: 3,
          })}
        </Typography>
      </Paper>
    );
  }

  const weakest = trajectory.weakestDimension;
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {t('socraticPractice.progress.title')}
      </Typography>
      <Stack spacing={1}>
        {trajectory.dimensions.map((d) => (
          <DimensionRow key={d.key} dim={d} />
        ))}
      </Stack>

      {trajectory.masteryByTopic.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t('socraticPractice.progress.topics')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {trajectory.masteryByTopic.map((m) => (
              <TopicChip
                key={m.standardId}
                title={topicTitles[m.standardId] ?? m.standardId.slice(0, 8)}
                status={m.status}
              />
            ))}
          </Stack>
        </>
      )}

      {weakest && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <EmojiObjectsRoundedIcon color="primary" fontSize="small" sx={{ mt: 0.2 }} />
            <Box>
              <Typography variant="caption" color="primary" fontWeight={700}>
                {t('socraticPractice.progress.nextChallenge')}
              </Typography>
              <Typography variant="body2">
                {t('socraticPractice.progress.nextChallengeBody', {
                  dim: t(`socraticPractice.progress.dim.${weakest}`),
                })}
              </Typography>
            </Box>
          </Stack>
        </>
      )}
    </Paper>
  );
}

function TopicChip({ title, status }: { title: string; status: TopicMasteryStatus }): JSX.Element {
  const { t } = useTranslation('sis');
  const color: Record<TopicMasteryStatus, string> = {
    in_progress: '#8a7fa6',
    consolidating: '#b5852f',
    mastered: '#2f6f4f',
  };
  return (
    <Chip
      size="small"
      label={`${title} · ${t(`socraticPractice.progress.mastery.${status}`)}`}
      sx={{ bgcolor: `${color[status]}22`, color: color[status], fontWeight: 600 }}
    />
  );
}

const TREND_ICON: Record<GrowthTrend, JSX.Element | null> = {
  growing: <TrendingUpRoundedIcon fontSize="small" sx={{ color: '#2f6f4f' }} />,
  steady: <TrendingFlatRoundedIcon fontSize="small" sx={{ color: '#8a7fa6' }} />,
  needs_work: <TrendingDownRoundedIcon fontSize="small" sx={{ color: '#b5852f' }} />,
  insufficient: null,
};

function DimensionRow({ dim }: { dim: DimensionTrajectoryDto }): JSX.Element {
  const { t } = useTranslation('sis');
  const [open, setOpen] = useState(false);
  const level = dim.currentLevel ?? 0;
  const hasEvidence = Boolean(dim.latestEvidence);
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ cursor: hasEvidence ? 'pointer' : 'default' }}
        onClick={() => hasEvidence && setOpen((o) => !o)}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          {t(`socraticPractice.progress.dim.${dim.key}`)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(`socraticPractice.progress.level.${level}`)}
        </Typography>
        {TREND_ICON[dim.trend]}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={(level / 4) * 100}
        sx={{ height: 6, borderRadius: 3, mt: 0.3 }}
      />
      {hasEvidence && (
        <Collapse in={open}>
          <Box sx={{ mt: 0.8, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('socraticPractice.progress.evidence')}
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              “{dim.latestEvidence!.quote}”
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dim.latestEvidence!.rationale}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
